// ContactView.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import ContactList from '../components/Contacts/ContactList';
import ContactForm from '../components/Contacts/ContactForm';
import ContactDetails from '../components/Contacts/ContactDetails';
import { Contact } from '../models';
import { getContacts, addContact, updateContact, deleteContact } from '../services/contacts.service';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const ContactView: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // On page load, fetch all contacts from Firestore.
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const contactsFromDB = await getContacts();
        setContacts(contactsFromDB);
      } catch (error) {
        console.error('Error fetching contacts:', error);
        setSnackbar({
          open: true,
          message: 'Error fetching contacts',
          severity: 'error',
        });
      }
    };

    fetchContacts();
  }, []);

  const handleSaveContact = async (contact: Contact) => {
    try {
      // Use the service to add the contact to Firestore.
      const savedContact = await addContact(contact);
      setContacts((prev) => [...prev, savedContact]);
      setIsAddDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Contact added successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving contact:', error);
      setSnackbar({
        open: true,
        message: 'Error saving contact',
        severity: 'error',
      });
    }
  };

  const handleUpdateContact = async (updatedContact: Contact) => {
    try {
      await updateContact(updatedContact);
      setContacts((prev) =>
        prev.map((c) => (c.id === updatedContact.id ? updatedContact : c))
      );
      if (selectedContact?.id === updatedContact.id) {
        setSelectedContact(updatedContact);
      }
      setSnackbar({
        open: true,
        message: 'Contact updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating contact:', error);
      setSnackbar({
        open: true,
        message: 'Error updating contact',
        severity: 'error',
      });
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      // Delete the contact from Firestore.
      await deleteContact(contactId);
  
      // Remove the contact from state.
      setContacts((prevContacts) => {
        const updatedContacts = prevContacts.filter((c) => c.id !== contactId);
        if (selectedContact?.id === contactId) {
          setSelectedContact(null);
        }
        return updatedContacts;
      });
      setSnackbar({
        open: true,
        message: 'Contact deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting contact:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting contact',
        severity: 'error',
      });
    }
  };

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
  };

  const handleBackToList = () => {
    setSelectedContact(null);
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Container sx={{ py: 4 }}>
      {selectedContact ? (
        // Details View
        <Box>
          <Button variant="outlined" onClick={handleBackToList} sx={{ mb: 2 }}>
            Back to List
          </Button>
          <ContactDetails
            contact={selectedContact}
            onUpdate={handleUpdateContact}
            onDelete={handleDeleteContact}
          />
        </Box>
      ) : (
        // List View
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h4">Contacts</Typography>
            <Button variant="contained" onClick={() => setIsAddDialogOpen(true)}>
              New Contact
            </Button>
          </Box>
          <ContactList contacts={contacts} onRowClick={handleSelectContact} />
        </Box>
      )}

      {/* Dialog for Adding a New Contact */}
      <Dialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <ContactForm onSave={handleSaveContact} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactView;
